import * as THREE from 'three';
import { shaderMaterial } from '@react-three/drei';
import { extend, ThreeElement } from '@react-three/fiber';

export interface IsolinesUniforms {
  boxMin: THREE.Vector3;
  boxMax: THREE.Vector3;
  showPositionColors: number;
  lineThickness: number;
  lineDensity: number;
  opacity: number;
  time: number;
}

export type IsolinesMaterialType = THREE.ShaderMaterial & {
  cameraMatrixWorld?: THREE.Matrix4;
  cameraProjectionMatrixInv?: THREE.Matrix4;
  lightPlaneNormal?: THREE.Vector3;
  lightPlaneConstant?: number;
  normalTexture?: THREE.Texture | null;
  depthTexture?: THREE.Texture | null;
  lightDir?: THREE.Vector3;
  near?: number;
  far?: number;
  modelMatrix?: THREE.Matrix4;
  worldRadius?: number;
  ior?: number;
  bounces?: number;
  resolution?: number;
  size?: number;
  intensity?: number;
  uniforms: {
    time: { value: number };
    boxMin: { value: THREE.Vector3 };
    boxMax: { value: THREE.Vector3 };
    showPositionColors: { value: number };
    lineDensity: { value: number };
    lineThickness: { value: number };
  };
};

export const IsolinesMaterial = /* @__PURE__ */ shaderMaterial(
  {
    boxMin: new THREE.Vector3(),
    boxMax: new THREE.Vector3(),
    showPositionColors: 0,
    lineDensity: 10,
    lineThickness: 1,
    opacity: 1,
    time: 0,
  },

  `
  varying vec3 vPos;

  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vPos = worldPos.xyz;
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
  `,
  // fragment shader
  `
  varying vec3 vPos;
  uniform vec3 boxMin;
  uniform vec3 boxMax;
  uniform float lineThickness;
  uniform float showPositionColors;
  uniform float time;
  uniform float opacity;
  uniform float lineDensity;

  void main() {
    vec3 col = vec3(0);
    col = (vPos - boxMin) / (boxMax - boxMin);
    col = clamp(col, 0., 1.);
    
    
    if (showPositionColors < 0.5) {
        // http://madebyevan.com/shaders/grid/
        float coord = (vPos.y + time * 0.5) / 2. * lineDensity;
        float grid = abs(fract(coord - 0.5) - 0.5) / fwidth(coord) / lineThickness;
        float line = min(grid, 1.0);
        //vec3 lineCol = mix(vec3(1, 1, 0),vec3(0, 1, 1), col.y);
        vec3 lineCol = vec3(1.0, 1.0, 1.0);
        col = mix(lineCol, gl_FragColor.rgb, line);
    }
    gl_FragColor = vec4(col, col * opacity);
  }
  `,
  (material) => {
    if (material) {
      // @ts-expect-error sets the derivatives extension
      material.extensions.derivatives = true;
    }
  },
);

declare module '@react-three/fiber' {
  interface ThreeElements {
    isolinesMaterial: ThreeElement<typeof IsolinesMaterial>;
  }
}

extend({ IsolinesMaterial });
